// Phase 4 follow-up #4 — Instagram picker modal.
//
// Drives the post-OAuth selection flow that previously only existed
// on the legacy /brand.html. After IG OAuth completes, the credential
// is in 'pending' status with a captured token but no Page / IG
// Business Account / Catalog binding yet. This modal lets the
// operator pick from the union of options the token can access and
// flips status to 'active' on save.
//
// Three optional steps:
//   1. Page  — required; pick a Facebook Page to bind to this brand
//   2. IG Business Account — auto-derived from the chosen page's
//      `igBusinessAccount` link; pre-selected and locked
//   3. Catalog — optional; pick from owned product catalogs across
//      the operator's businesses, or leave blank
//
// Wires:
//   GET /api/integrations/instagram/:credId/options
//   PATCH /api/integrations/instagram/:credId/selection
//
// Also doubles as the CHANGE-ACCOUNT flow for an already-ACTIVE credential —
// the selection PATCH is re-callable, and `options.current` (this
// credential's existing binding) tells us we're rebinding rather than
// connecting for the first time. Picking a Page whose IG account differs
// from `current.igUserId` is a real account switch: we warn about it before
// save (old synced posts are kept, new ones need a fresh sync), and if the
// backend refuses with 409 `ig-account-bound-elsewhere` — the chosen IG
// account already belongs to a DIFFERENT active credential, which Meta's
// one-credential-per-account model disallows — we offer an explicit
// disconnect-then-retry instead of dead-ending on a red error box. See
// `resolveConflict` below.
//
//   DELETE /api/integrations/instagram/:conflictCredentialId  (conflict only)

import { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Box, VStack, HStack, Text, Heading, Button, Spinner, Badge, Radio, RadioGroup,
  Flex, useToast, Icon
} from '@chakra-ui/react';
import { apiJson, isApiError } from '../../auth/apiFetch';

type Page = {
  id:                string;
  name:              string;
  igBusinessAccount: { id: string; username: string | null } | null;
};

type Catalog = {
  id:           string;
  name:         string;
  businessName: string;
  productCount: number | null;
};

type OptionsResponse = {
  options: { pages: Page[]; catalogs: Catalog[] };
  current: { pageId: string | null; igUserId: string | null; catalogId: string | null };
};

// The 409 body the backend sends when the chosen Page's IG Business Account
// is already bound to a DIFFERENT active credential — verified against
// routes/integrations.js: the conflict lookup is scoped to the SAME
// brandId, so this is always a stale/duplicate credential left over on
// THIS brand (e.g. an earlier connect/reconnect cycle), never another
// brand. Meta allows only one active credential per IG Business Account,
// so resolving this requires an explicit, named disconnect of the other
// one — see `resolveConflict`.
type IgAccountConflict = {
  error:                string;
  code:                 'ig-account-bound-elsewhere';
  conflictCredentialId: string;
  igUserId:             string;
  igUsername:           string | null;
};

type Props = {
  isOpen:        boolean;
  onClose:       () => void;
  credentialId:  string | null;
  onCompleted:   () => void;
};

export function IGPickerModal({ isOpen, onClose, credentialId, onCompleted }: Props) {
  const toast = useToast();
  const [opts, setOpts]       = useState<OptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [pageId, setPageId]       = useState<string | null>(null);
  const [catalogId, setCatalogId] = useState<string | null>(null);
  const [saving, setSaving]       = useState(false);
  // Set when save() gets the structured 409 described in the file banner.
  // Null the rest of the time. Kept separate from `error` so the picker
  // form stays on screen with a resolvable action, instead of the plain
  // error box replacing it and dead-ending the flow (see the render below).
  const [conflict, setConflict]   = useState<IgAccountConflict | null>(null);
  // Busy flag for the disconnect-then-retry sequence in resolveConflict —
  // separate from `saving` so the conflict box's own button can carry its
  // own loading state without touching the (otherwise idle) Save & Activate
  // button.
  const [resolving, setResolving] = useState(false);

  // Fetch options when the modal opens
  useEffect(() => {
    if (!isOpen || !credentialId) { setOpts(null); return; }
    setLoading(true);
    setError(null);
    setConflict(null);
    setPageId(null);
    setCatalogId(null);
    void (async () => {
      try {
        const res = await apiJson<OptionsResponse>(`/api/integrations/instagram/${credentialId}/options`);
        setOpts(res);
        setPageId(res.current?.pageId || null);
        setCatalogId(res.current?.catalogId || null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load options');
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, credentialId]);

  const selectedPage = opts?.options.pages.find(p => p.id === pageId) || null;
  const igUserId     = selectedPage?.igBusinessAccount?.id || null;

  // True once this credential already has a bound IG account — i.e. we're
  // changing an existing connection rather than connecting for the first
  // time. Drives the header copy and the account-switch warning below.
  const isRebind = !!opts?.current?.igUserId;
  // Best-effort label for the ALREADY-bound account: find which page in
  // this same options list carries it, so we can name it by username
  // instead of a bare numeric id. Falls back to a generic label if that
  // page isn't in the token's accessible set any more.
  const currentPage     = opts?.options.pages.find(p => p.igBusinessAccount?.id === opts?.current?.igUserId) || null;
  const oldAccountLabel = currentPage?.igBusinessAccount?.username ? `@${currentPage.igBusinessAccount.username}` : 'the previously connected account';
  const newAccountLabel = selectedPage?.igBusinessAccount?.username ? `@${selectedPage.igBusinessAccount.username}` : 'the new account';
  // Only warn about a real account switch — a Page with no linked IG
  // account at all is already explained by Section 2's empty state below.
  const isSwitchingAccount = isRebind && !!igUserId && igUserId !== opts?.current?.igUserId;

  // Shared by save() and resolveConflict() — both end in the identical
  // success behaviour once the selection is actually persisted.
  const finishSuccess = () => {
    toast({
      title: 'Instagram linked',
      description: selectedPage?.igBusinessAccount?.username
        ? `Connected to @${selectedPage.igBusinessAccount.username}`
        : `Connected to ${selectedPage?.name}`,
      status: 'success',
      duration: 3000
    });
    onCompleted();
    onClose();
  };

  const save = async () => {
    if (!credentialId || !pageId) return;
    setSaving(true);
    setError(null);
    setConflict(null);
    try {
      await apiJson(`/api/integrations/instagram/${credentialId}/selection`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageId, igUserId, catalogId })
      });
      finishSuccess();
    } catch (err: unknown) {
      // A 409 with this specific code is not a dead end — see the file
      // banner. Any other status/shape (including other 409 bodies) falls
      // through to the plain error box exactly like before. Validate the
      // shape (not just truthiness) before trusting it: this id drives a
      // real DELETE call below, so a malformed body should fall through to
      // the generic error instead of firing that DELETE at a garbage path.
      if (isApiError(err) && err.status === 409) {
        const body = err.body as Partial<IgAccountConflict> | null;
        if (
          body?.code === 'ig-account-bound-elsewhere' &&
          typeof body.conflictCredentialId === 'string' && body.conflictCredentialId &&
          typeof body.igUserId === 'string' && body.igUserId
        ) {
          setConflict({
            error:                body.error || 'That Instagram account is already connected on another credential.',
            code:                 'ig-account-bound-elsewhere',
            conflictCredentialId: body.conflictCredentialId,
            igUserId:             body.igUserId,
            igUsername:           typeof body.igUsername === 'string' ? body.igUsername : null
          });
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // The operator clicked the named "Disconnect @x and continue" button on
  // the conflict box below — this is never triggered automatically.
  //
  // Deliberately split into two stages rather than one try/catch. Once the
  // DELETE lands, a real side effect has happened — the other credential is
  // actually gone — so a failure from here on has to say that explicitly
  // instead of reusing the same generic wording a "nothing happened yet"
  // failure gets. Collapsing the two is how an operator ends up clicking
  // Cancel thinking the whole thing was a no-op, when the account has, in
  // fact, already been freed.
  const resolveConflict = async () => {
    if (!conflict || !credentialId || !pageId) return;
    const conflictUsername = conflict.igUsername; // capture — conflict is cleared before the retry
    setResolving(true);
    setError(null);
    try {
      await apiJson(`/api/integrations/instagram/${conflict.conflictCredentialId}`, { method: 'DELETE' });
    } catch (err: unknown) {
      // Nothing committed yet — behaves exactly like any other save failure.
      setError(err instanceof Error ? err.message : 'Failed to disconnect the other credential');
      setResolving(false);
      return;
    }
    // The disconnect landed. Clear `conflict` regardless of what happens
    // next — the credential it names is gone, so a second click on this
    // same box would just 404 instead of doing anything useful.
    setConflict(null);
    try {
      await apiJson(`/api/integrations/instagram/${credentialId}/selection`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageId, igUserId, catalogId })
      });
      finishSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      setError(
        `Disconnected ${conflictUsername ? `@${conflictUsername}` : 'the other credential'} — that account is free ` +
        `now — but finishing the switch failed: ${msg}. Click Save & Activate to retry.`
      );
    } finally {
      setResolving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={(saving || resolving) ? () => {} : onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <IGIcon />
            <Box>
              <Heading size="md">{isRebind ? 'Change Instagram account' : 'Connect Instagram'}</Heading>
              <Text fontSize="xs" color="brand.muted" fontWeight="400">
                Pick a Facebook Page (and optionally a Catalog) to bind to this brand.
                {isRebind && ' Picking a different Page switches the Instagram account this brand syncs from.'}
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={saving || resolving} />
        <ModalBody>
          {loading ? (
            <Flex py={8} align="center" justify="center"><Spinner /></Flex>
          ) : error ? (
            <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={3}>
              <Text fontSize="sm" color="red.700">{error}</Text>
            </Box>
          ) : opts ? (
            <VStack align="stretch" spacing={6}>
              <Section title="1 · Facebook Page" required>
                {opts.options.pages.length === 0 ? (
                  <EmptyState text="No Facebook Pages accessible with this token. Re-authorize and grant the requested scopes." />
                ) : (
                  // Clearing `conflict` here too: it names a specific Page's
                  // IG account, so it goes stale the moment the operator
                  // picks a different one — next Save will surface a fresh
                  // conflict if the new pick still collides with something.
                  <RadioGroup value={pageId || ''} onChange={(v) => { setPageId(v); setConflict(null); }}>
                    <VStack align="stretch" spacing={2}>
                      {opts.options.pages.map(p => (
                        <PageRow key={p.id} page={p} selected={p.id === pageId} />
                      ))}
                    </VStack>
                  </RadioGroup>
                )}
              </Section>

              <Section title="2 · Instagram Business Account" optional>
                {!selectedPage ? (
                  <EmptyState text="Pick a Page first." />
                ) : selectedPage.igBusinessAccount ? (
                  <HStack
                    bg="rsViolet.50"
                    borderWidth="1px"
                    borderColor="rsViolet.200"
                    borderRadius="md"
                    p={3}
                    spacing={3}
                  >
                    <CheckIcon />
                    <Box>
                      <Text fontSize="sm" fontWeight="700" color="brand.ink">
                        @{selectedPage.igBusinessAccount.username || '(no username)'}
                      </Text>
                      <Text fontSize="xs" color="brand.muted">
                        Auto-derived from the selected Page's linked IG business account.
                      </Text>
                    </Box>
                  </HStack>
                ) : (
                  <EmptyState text="The selected Page has no linked Instagram Business Account. Catalog and post syncs will skip this credential." />
                )}
              </Section>

              <Section title="3 · Product Catalog" optional>
                {opts.options.catalogs.length === 0 ? (
                  <EmptyState text="No product catalogs accessible. You can still connect Instagram and skip this step — catalog matching will run on the brand's other catalog sources." />
                ) : (
                  <RadioGroup value={catalogId || ''} onChange={(v) => setCatalogId(v || null)}>
                    <VStack align="stretch" spacing={2}>
                      <Box
                        borderWidth="1px"
                        borderColor={catalogId ? 'brand.border' : 'rsViolet.200'}
                        bg={catalogId ? 'brand.surface' : 'rsViolet.50'}
                        borderRadius="md"
                        p={3}
                      >
                        <Radio value="">
                          <Text fontSize="sm" fontWeight="600">Skip catalog</Text>
                          <Text fontSize="xs" color="brand.muted">No Meta product catalog binding</Text>
                        </Radio>
                      </Box>
                      {opts.options.catalogs.map(c => (
                        <CatalogRow key={c.id} catalog={c} selected={c.id === catalogId} />
                      ))}
                    </VStack>
                  </RadioGroup>
                )}
              </Section>

              {/* Informational only — fires purely off the current selection,
                  independently of whether a save has even been attempted yet.
                  See the file banner for why this doesn't block Save. */}
              {isSwitchingAccount && (
                <Box bg="orange.50" borderWidth="1px" borderColor="orange.300" borderRadius="md" p={3}>
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="0.06em">
                      Switching Instagram accounts
                    </Text>
                    <Text fontSize="xs" color="brand.ink">
                      Posts already synced from {oldAccountLabel} stay in the media library — they are not
                      removed. Posts from {newAccountLabel} are not pulled in until a sync runs.
                    </Text>
                  </VStack>
                </Box>
              )}

              {/* Set only by the specific 409 in save()/resolveConflict() —
                  every other error still goes through the plain red box
                  above (which replaces this whole form; see file banner). */}
              {conflict && (
                <Box bg="orange.50" borderWidth="1px" borderColor="orange.300" borderRadius="md" p={3}>
                  <VStack align="stretch" spacing={2}>
                    <Box>
                      <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="0.06em">
                        Already connected elsewhere
                      </Text>
                      <Text fontSize="sm" color="brand.ink" mt={1}>{conflict.error}</Text>
                    </Box>
                    <HStack justify="flex-end" spacing={2}>
                      <Button size="xs" variant="ghost" onClick={() => setConflict(null)} isDisabled={resolving}>
                        Never mind
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="orange"
                        onClick={resolveConflict}
                        isLoading={resolving}
                        loadingText="Disconnecting…"
                      >
                        {conflict.igUsername ? `Disconnect @${conflict.igUsername} and continue` : 'Disconnect the other credential and continue'}
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}
            </VStack>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose} isDisabled={saving || resolving}>Cancel</Button>
          <Button variant="brand" onClick={save} isLoading={saving} isDisabled={!pageId || resolving}>
            Save & Activate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function Section({ title, required, optional, children }: { title: string; required?: boolean; optional?: boolean; children: React.ReactNode }) {
  return (
    <Box>
      <HStack mb={2}>
        <Heading size="xs">{title}</Heading>
        {required && <Badge fontSize="9px" colorScheme="red" variant="subtle">Required</Badge>}
        {optional && <Badge fontSize="9px" colorScheme="gray" variant="subtle">Optional</Badge>}
      </HStack>
      {children}
    </Box>
  );
}

function PageRow({ page, selected }: { page: Page; selected: boolean }) {
  return (
    <Box
      borderWidth="1px"
      borderColor={selected ? 'rsViolet.400' : 'brand.border'}
      bg={selected ? 'rsViolet.50' : 'brand.surface'}
      borderRadius="md"
      p={3}
    >
      <Radio value={page.id}>
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="700">{page.name}</Text>
          {page.igBusinessAccount && (
            <Badge fontSize="9px" colorScheme="purple" variant="subtle">
              @{page.igBusinessAccount.username || page.igBusinessAccount.id}
            </Badge>
          )}
        </HStack>
        <Text fontSize="10px" color="brand.muted" fontFamily="mono">{page.id}</Text>
      </Radio>
    </Box>
  );
}

function CatalogRow({ catalog, selected }: { catalog: Catalog; selected: boolean }) {
  return (
    <Box
      borderWidth="1px"
      borderColor={selected ? 'rsViolet.400' : 'brand.border'}
      bg={selected ? 'rsViolet.50' : 'brand.surface'}
      borderRadius="md"
      p={3}
    >
      <Radio value={catalog.id}>
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="700">{catalog.name || '(unnamed)'}</Text>
          {catalog.productCount != null && (
            <Badge fontSize="9px" colorScheme="blue" variant="subtle">{catalog.productCount.toLocaleString()} products</Badge>
          )}
        </HStack>
        <Text fontSize="10px" color="brand.muted">{catalog.businessName} · <Text as="span" fontFamily="mono">{catalog.id}</Text></Text>
      </Radio>
    </Box>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <Box bg="gray.50" borderRadius="md" p={3}>
      <Text fontSize="xs" color="brand.muted" fontStyle="italic">{text}</Text>
    </Box>
  );
}

function IGIcon() {
  return (
    <Box
      w="32px" h="32px"
      borderRadius="md"
      bgImage="linear-gradient(135deg, #FEDA77, #F58529, #DD2A7B, #8134AF, #515BD4)"
      display="flex" alignItems="center" justifyContent="center"
      color="white" fontSize="xs" fontWeight="800"
    >
      IG
    </Box>
  );
}

function CheckIcon() {
  return <Icon viewBox="0 0 24 24" w="20px" h="20px" color="rsViolet.500"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" /></Icon>;
}
